import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiClient } from "@/api";
import { User, OAuthConfig, TokenResponse, Project } from "@/api/types";
import { success, error as showError, info } from "@/utils/toast";

interface ProjectContextType {
  project: Project | null;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  projectId?: string;
  children: ReactNode;
}

export function ProjectProvider({ projectId, children }: ProjectProviderProps) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCurrentProject();
  }, [projectId]);

  const fetchCurrentProject = async () => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      return;
    }
    
    try {
      const userData = await apiClient.get<Project>(`/projects/${projectId}/`);
      setProject(userData);
    } catch (error) {
      console.error("Failed to fetch current project:", error);
    } finally {
      setLoading(false);
    }
  };

  const value: ProjectContextType = {
    project,
    loading
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error("useProject must be used within an ProjectProvider");
  }
  return context;
}
